import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { useLatestRef } from '@heidi/utils';
import { useEffect, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';

/**
 * The list commits queries on a 250ms debounce, so a user pausing mid-word
 * briefly commits prefixes ("sm" → "smi" → "smith"). Requiring the query to
 * stay the active, settled search this long before counting drops those
 * prefixes, so the baseline count and query_length distribution reflect
 * searches the user rested on rather than keystroke-pause artifacts.
 */
const TRACKING_SETTLE_MS = 1000;

interface UseSessionSearchTrackingParams {
  /** The committed (debounced) search query driving the list request. */
  searchQuery: string;
  /** Rows currently loaded for the active query. */
  resultCount: number;
  /** True once the list response for the active query is fresh (not placeholder). */
  isSettled: boolean;
}

/**
 * Emits `session_search_usage_baseline` once per search the user rests on: the
 * query must stay the active, settled search for `TRACKING_SETTLE_MS` before it
 * counts, so quickly-superseded prefixes never fire. Latching on the query
 * value keeps pagination, background polls, and filter-driven refetches of the
 * same query from re-emitting; clearing the search re-arms the latch so
 * repeating a search counts as a new search action.
 *
 * The event omits `total_sessions_loaded`: the new list searches server-side,
 * so there is no unfiltered client-side total to report.
 */
export function useSessionSearchTracking({
  searchQuery,
  resultCount,
  isSettled,
}: UseSessionSearchTrackingParams): void {
  const lastTrackedQueryRef = useRef('');

  // Read at settle, not as an effect dep, so later row growth (pagination, a
  // late-resolving group) neither restarts the settle window nor changes the
  // reported count.
  const resultCountRef = useLatestRef(resultCount);

  const debouncedTrack = useDebouncedCallback((query: string, count: number) => {
    if (lastTrackedQueryRef.current === query) return;
    lastTrackedQueryRef.current = query;
    track({
      event_name: TrackingEventName.SessionSearchUsageBaseline,
      props: {
        query_length: query.length,
        result_count: count,
      },
      options: { amplitude: false },
    });
  }, TRACKING_SETTLE_MS);

  useEffect(() => {
    if (!searchQuery) {
      debouncedTrack.cancel();
      lastTrackedQueryRef.current = '';
      return;
    }
    // A just-changed query hasn't refetched yet — drop the pending emit for the query it replaced.
    if (!isSettled) {
      debouncedTrack.cancel();
      return;
    }
    debouncedTrack(searchQuery, resultCountRef.current);
  }, [searchQuery, isSettled, debouncedTrack, resultCountRef]);
}
